import { APP_INITIALIZER, NgModule, isDevMode } from '@angular/core';
import {
  BrowserModule,
} from '@angular/platform-browser';
import { RouterModule, NoPreloading, RouteReuseStrategy } from '@angular/router';
import { TabRouteReuseStrategy } from './base/shell/tab-route-reuse.strategy';
import { AppComponent } from './app.component';
import { provideTransloco } from '@jsverse/transloco';
import { ErsTranslocoLoader } from './transloco-loader';
import {
  SHIPPED_UI_LANGUAGES,
  UiLanguageService,
} from './services/language/ui-language.service';
import { appRoutes } from './app.routes';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { LocationStrategy } from '@angular/common';
import { ModuleUrlAliasLocationStrategy } from './base/services/module-url-alias/module-url-alias-location.strategy';
import { provideRouterStore, RouterState } from '@ngrx/router-store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { metaReducers, reducers } from './+store/root.reducer';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { NgProgressModule } from 'ngx-progressbar';
import { OAuthModule } from 'angular-oauth2-oidc';
import { ErsAuthInterceptor } from './interceptors/ers-auth-interceptor';
import { UnsavedWorkInterceptor } from './interceptors/unsaved-work.interceptor';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
} from '@angular/material/core';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MAT_MOMENT_DATE_FORMATS,
} from '@angular/material-moment-adapter';
import { LocalTodayMomentDateAdapter } from './local-today-date.adapter';
import { APP_ENVIRONMENT } from '@rcs/shared';
import { ERS_GRID_ID_RESOLVER, ERS_GRID_PREFERENCE_STORE } from '@rcs/grid';
import { LmHttpAdapter } from '@rcs/leave-management';
import { RouteErsGridIdResolver } from './services/ers-grid-id-resolver';
import { ApiErsGridPreferenceStore } from './services/api-grid-preference-store';
import { AppLmHttpAdapter } from './modules/ers-leave-management/services/app-lm-http-adapter';
import {
  CHAT_API_BASE_URL,
  CONNECT_API_BASE_URL,
  CONNECT_CALL_SOCKET_ENABLED,
  CONNECT_DOCK_ENABLED,
  CONNECT_TEAMS_CALLING_ENABLED,
  ConnectHttpAdapter,
  UM_API_BASE_URL,
} from '@rcs/ers-connect';
import { AppConnectHttpAdapter } from './modules/ers-connect/services/app-connect-http-adapter';
import { environment } from '../environments/environment';
import { 
  NotifyModule, 
  ApiNotificationOverlayComponent,
  ApiResponseNotificationClassInterceptor 
} from '@rcs/notify';


@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    RouterModule.forRoot(appRoutes, {
      preloadingStrategy: NoPreloading
    }),
    // ServiceWorkerModule.register('ngsw-worker.js', {
    //   enabled: !isDevMode(),
    //   // Register the ServiceWorker as soon as the application is stable
    //   // or after 30 seconds (whichever comes first).
    //   registrationStrategy: 'registerWhenStable:30000',
    // }),
    StoreModule.forRoot(reducers, {
      metaReducers,
      runtimeChecks: {
        strictActionImmutability: true,
        strictStateImmutability: true,
      },
    }),
    EffectsModule.forRoot([]),
    /**
     * Redux DevTools, and only where somebody is debugging.
     *
     * `instrument()` used to be called unconditionally. `logOnly` narrows
     * what DevTools may do and does not stop it running, so production
     * shipped the instrumentation: every dispatched action and a snapshot
     * of store state, held in memory and offered to the DevTools browser
     * extension of anyone who had it installed. That is the whole state of
     * an ERP - ledgers, payroll, customers - handed to an extension on a
     * machine we do not control.
     *
     * Keyed on `environment.production`, so dev and QA keep DevTools and
     * production, stage and offline do not. Deliberately not `isDevMode()`:
     * that follows the build's optimization flag, which is on for the
     * deployed dev build, and would take DevTools away from the dev
     * environment where it is most used.
     *
     * This is also where the NG0600 errors on load come from - DevTools
     * writes its own state signal from inside a reactive context, which is
     * an open NgRx bug (ngrx/platform#3932) and not ours. They stop
     * appearing wherever DevTools is off. Silencing them on dev would mean
     * giving up DevTools there, which is not a trade worth making for
     * console noise.
     */
    ...(environment.production ? [] : [StoreDevtoolsModule.instrument({ logOnly: !isDevMode() })]),
    NgProgressModule.withConfig({
      color: '#f71cff',
      thick: true,
    }),
    OAuthModule.forRoot(),
    NotifyModule, // Register NgRx reducers and effects for notifications
    ApiNotificationOverlayComponent, // Standalone component for API notifications
  ],
  providers: [
    /* Keeps a tabbed screen alive while another tab is shown. Angular renders
       one routed component at a time and destroys it on navigate, so without
       this a tab switch would rebuild the screen and lose its grid state,
       scroll position and unsaved edits. Only routes the workspace holds open
       are detached; everything else keeps Angular's default behaviour. */
    { provide: RouteReuseStrategy, useClass: TabRouteReuseStrategy },
    { provide: LocationStrategy, useClass: ModuleUrlAliasLocationStrategy },
    /* Static UI text. Catalogues are generated per release and fetched once,
       so a language change costs one request, not a rebuild. reRenderOnLangChange
       is on because the language is a user preference that can change without a
       reload. */
    provideTransloco({
      config: {
        availableLangs: [...SHIPPED_UI_LANGUAGES],
        defaultLang: 'en',
        fallbackLang: 'en',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
        missingHandler: { allowEmpty: false, logMissingKey: isDevMode(), useFallbackTranslation: true },
      },
      loader: ErsTranslocoLoader,
    }),
    /* Re-apply the language chosen last time before anything renders, so a
       reload does not show English and then repaint. The authoritative value
       still arrives with the user's configuration; this only replays it. */
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [UiLanguageService],
      useFactory: (lang: UiLanguageService) => () => lang.restore(),
    },
    // provideClientHydration(), Since you're not using SSR, you do not need to include
    provideAnimationsAsync(),
    provideRouterStore({
      routerState: RouterState.Minimal,
    }),
    provideHttpClient(withInterceptorsFromDi()),
    { provide: APP_ENVIRONMENT, useValue: { apiUrl: environment.apiUrl, isOfflineHosted: environment.isOfflineHosted } },

    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErsAuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: UnsavedWorkInterceptor,
      multi: true,
    },
    // {   // CHANDU: Commenting this interceptor as it is showing duplicate notifications for API responses. We need to analyze and fix the root cause for duplicate notifications before enabling this interceptor.
    //   provide: HTTP_INTERCEPTORS,
    //   useClass: ApiResponseNotificationClassInterceptor,
    //   multi: true,
    // },
    // Bug 30920: LocalTodayMomentDateAdapter keeps useUtc semantics but anchors
    // today() to the user's local calendar date (base adapter returns moment.utc()).
    { provide: DateAdapter, useClass: LocalTodayMomentDateAdapter },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    // Pairs with the Moment adapter above. MAT_DATE_FORMATS has no default, and
    // SharedImportsModule used to supply the moment formats as a side effect of
    // its provideMomentDateAdapter() — which is exactly the provider that was
    // shadowing the adapter, so it had to go. Provide the formats here instead,
    // where they belong alongside the adapter they match.
    { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS },
    // Activate per-user grid persistence on every <ers-grid> (route-keyed) unless a
    // page sets its own [gridId] or [persistState]="false".
    { provide: ERS_GRID_ID_RESOLVER, useExisting: RouteErsGridIdResolver },
    // Persist grid prefs to the backend API (cross-browser/device) instead of localStorage.
    { provide: ERS_GRID_PREFERENCE_STORE, useExisting: ApiErsGridPreferenceStore },
    // Leave Management HTTP adapter
    { provide: LmHttpAdapter, useClass: AppLmHttpAdapter },
    // Connect HTTP adapter + service base URLs
    { provide: ConnectHttpAdapter, useClass: AppConnectHttpAdapter },
    { provide: CHAT_API_BASE_URL, useValue: environment.apiUrl.chat },
    { provide: CONNECT_API_BASE_URL, useValue: environment.apiUrl.connect },
    { provide: CONNECT_CALL_SOCKET_ENABLED, useValue: environment.featureFlags?.connectCallSocket === true },
    { provide: CONNECT_DOCK_ENABLED, useValue: environment.featureFlags?.connectDock === true },
    {
      provide: CONNECT_TEAMS_CALLING_ENABLED,
      useValue: environment.featureFlags?.connectTeamsCalling === true,
    },
    { provide: UM_API_BASE_URL, useValue: environment.apiUrl.userManagement },
  ],
  exports: [],
})
export class AppModule {}
